import type { ReactNode } from 'react';
import type { Tone } from '@shared/config/theme';
import { toneTextClasses } from '@shared/utils/statusTone';

interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** Secondary line under the value (delta, window, count). */
  meta?: ReactNode;
  /** Tints `meta` as a positive/negative movement. */
  deltaTone?: Extract<Tone, 'ok' | 'warn' | 'neutral'>;
  icon?: ReactNode;
  className?: string;
}

/**
 * Single cell of the 4-column KPI grid (see `qa-screenshots/layout-hybrid.html`).
 * Visual spec: layered surface, consistent border/radius, readable label,
 * and a tabular value that scales down on narrow cards.
 */
export function KpiCard({
  label,
  value,
  meta,
  deltaTone = 'neutral',
  icon,
  className = '',
}: KpiCardProps) {
  return (
    <div
      className={`min-w-0 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl px-3.5 py-3 flex flex-col gap-1 ${className}`}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
        {icon}
        <span className="truncate" title={label}>
          {label}
        </span>
      </div>
      <div className="text-xl sm:text-2xl font-semibold tracking-tight tabular-nums text-slate-900 dark:text-white leading-tight break-words">
        {value}
      </div>
      {meta && (
        <div className={`text-xs font-medium truncate ${toneTextClasses[deltaTone]}`}>{meta}</div>
      )}
    </div>
  );
}

export default KpiCard;
